import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  ChevronRight, 
  ChevronDown, 
  Wand2, 
  Sparkles, 
  Repeat, 
  Shuffle, 
  Hash, 
  BarChart2,
  X,
  Check,
  Loader2,
  Zap,
  Pen,
  AlertTriangle
} from 'lucide-react';
import { usePostingStore, PlatformId } from '../../lib/store/usePostingStore';
import { streamOpenAIResponse } from '../../lib/openai';

interface AiTool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface CaptionSuggestion {
  id: string;
  text: string;
  isOverLimit: boolean;
  overrunAmount?: number;
}

interface AiToolResult {
  id: string;
  toolId: string;
  title: string;
  content: string;
  expanded: boolean;
}

const Step3CaptionAutomations: React.FC = () => {
  const { 
    platforms, 
    captions, 
    setCaption, 
    nextStep, 
    prevStep 
  } = usePostingStore();
  
  const [activeTab, setActiveTab] = useState<PlatformId | null>(
    platforms.find(p => p.enabled)?.id || null
  );
  
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [showQuickActions, setShowQuickActions] = useState<boolean>(false);
  const [runningTool, setRunningTool] = useState<string | null>(null);
  const [toolResults, setToolResults] = useState<AiToolResult[]>([]);
  const [captionSuggestions, setCaptionSuggestions] = useState<CaptionSuggestion[]>([]);
  const [numCaptions, setNumCaptions] = useState<number>(3);
  const [feedbackInput, setFeedbackInput] = useState<string>('');
  const [activeFeedbackId, setActiveFeedbackId] = useState<string | null>(null);
  
  const enabledPlatforms = platforms.filter(p => p.enabled);
  
  // If no platforms are enabled, redirect back to step 1
  if (enabledPlatforms.length === 0) {
    prevStep();
    return null;
  }
  
  const aiTools: AiTool[] = [
    {
      id: 'hashtag-gen',
      name: 'Hashtag Generator',
      description: 'Generate relevant hashtags',
      icon: <Hash className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    },
    {
      id: 'caption-ai',
      name: 'Caption AI',
      description: 'Generate or improve captions',
      icon: <Pen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
    }
  ];
  
  const getPlatformConfig = (platformId: PlatformId) => {
    return platforms.find(p => p.id === platformId);
  };
  
  const handleRunTool = (toolId: string) => {
    setRunningTool(toolId);
    
    // Simulate AI processing
    setTimeout(() => {
      const tool = aiTools.find(t => t.id === toolId);
      if (!tool) return;
      
      const newResult: AiToolResult = {
        id: `result-${Date.now()}`,
        toolId,
        title: `${tool.name} Result`,
        content: generateDummyContent(toolId),
        expanded: false
      };
      
      setToolResults(prev => [newResult, ...prev]);
      setRunningTool(null);
    }, 2000);
  };
  
  const generateDummyContent = (toolId: string): string => {
    switch (toolId) {
      case 'post-ideas':
        return "1. Share a behind-the-scenes look at your creative process\n2. Create a 'day in the life' post showing your workflow\n3. Ask your audience a question related to your industry";
      case 'carousel-builder':
        return "Slide 1: Introduction - Hook your audience with a bold statement\nSlide 2: Problem - Identify the pain point\nSlide 3: Solution - Present your unique approach\nSlide 4: Benefits - List key advantages\nSlide 5: Call to action";
      case 'repurpose-email':
        return "Subject: Exclusive Insights: What We Learned This Month\n\nHi [Name],\n\nI wanted to share some exclusive insights from our recent project that I think you'll find valuable...";
      case 'ab-tester':
        return "Hook A: \"Want to increase your engagement by 200%?\"\nHook B: \"I tried 5 different strategies and this one tripled my engagement\"";
      case 'hashtag-gen':
        return "#contentcreator #socialmediatips #digitalmarketing #growyourbusiness #entrepreneur #socialmediamarketing #contentcreation #marketingstrategy";
      case 'caption-analyzer':
        return "Score: 85/100\nStrengths: Good use of keywords, appropriate length, clear call-to-action\nAreas to improve: Add more engagement hooks, consider adding relevant hashtags";
      default:
        return "Generated content will appear here";
    }
  };
  
  const toggleResultExpansion = (resultId: string) => {
    setToolResults(prev => 
      prev.map(result => 
        result.id === resultId 
          ? { ...result, expanded: !result.expanded } 
          : result
      )
    );
  };
  
  const applyResult = (result: AiToolResult) => {
    if (!activeTab) return;
    
    let newContent = '';
    
    switch (result.toolId) {
      case 'hashtag-gen':
        newContent = `${captions[activeTab] || ''}\n\n${result.content}`;
        break;
      default:
        newContent = result.content;
    }
    
    setCaption(activeTab, newContent);
  };
  
  const removeResult = (resultId: string) => {
    setToolResults(prev => prev.filter(result => result.id !== resultId));
  };

  const getCharacterLimit = (platformId: PlatformId): number => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.characterLimits.caption || 280; // Default to Twitter's limit
  };

  const handleCaptionAI = async () => {
    if (!activeTab) return;
    
    try {
      setRunningTool('caption-ai');
      
      const currentCaption = captions[activeTab] || '';
      const characterLimit = getCharacterLimit(activeTab);
      const isGenerateMode = !currentCaption.trim();
      const mediaContext = files.length > 0 ? 
        `File named "${files[0].name}" of type "${files[0].type}"` : 
        'Unknown media';
      
      let prompt = '';
      
      if (isGenerateMode) {
        prompt = `Create ${numCaptions} captions that fit this media context: ${mediaContext}.`;
      } else {
        prompt = `Improve this caption, keeping its original intent: ${currentCaption}`;
      }
      
      const systemPrompt = `You are a world-class social-media copywriter. Output ONLY a JSON array called captions, each element a one-sentence caption within ${characterLimit} characters and written in natural, engaging English. Do not include hashtags.`;
      
      let responseText = '';
      
      await streamOpenAIResponse(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        (chunk) => {
          responseText += chunk;
          try {
            // Try to parse the response as JSON
            const jsonStart = responseText.indexOf('{');
            const jsonEnd = responseText.lastIndexOf('}');
            
            if (jsonStart >= 0 && jsonEnd > jsonStart) {
              const jsonStr = responseText.substring(jsonStart, jsonEnd + 1);
              const data = JSON.parse(jsonStr);
              
              if (Array.isArray(data.captions)) {
                const suggestions = data.captions.map((caption: string, index: number) => ({
                  id: `caption-${Date.now()}-${index}`,
                  text: caption,
                  isOverLimit: caption.length > characterLimit,
                  overrunAmount: caption.length > characterLimit ? caption.length - characterLimit : undefined
                }));
                
                setCaptionSuggestions(suggestions);
              }
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          console.error('Error generating captions:', error);
        }
      );
      
      // Create a tool result for the caption suggestions
      const newResult: AiToolResult = {
        id: `result-${Date.now()}`,
        toolId: 'caption-ai',
        title: isGenerateMode ? `${numCaptions} captions ready` : 'Improved caption ready',
        content: isGenerateMode ? 
          `Generated ${captionSuggestions.length} captions based on your media` : 
          'Improved version of your caption',
        expanded: false
      };
      
      setToolResults(prev => [newResult, ...prev]);
      
    } catch (err) {
      console.error('Error running Caption AI:', err);
    } finally {
      setRunningTool(null);
    }
  };

  const handleRefineCaptionFeedback = async (captionId: string, feedback: string) => {
    if (!activeTab) return;
    
    try {
      setRunningTool('caption-ai');
      
      const captionToRefine = captionSuggestions.find(c => c.id === captionId);
      if (!captionToRefine) return;
      
      const characterLimit = getCharacterLimit(activeTab);
      
      const prompt = `User feedback: ${feedback}. Provide ONE new caption only based on this caption: "${captionToRefine.text}"`;
      
      const systemPrompt = `You are a world-class social-media copywriter. Output ONLY a JSON object with a single "caption" field containing a one-sentence caption within ${characterLimit} characters and written in natural, engaging English. Do not include hashtags.`;
      
      let responseText = '';
      
      await streamOpenAIResponse(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        (chunk) => {
          responseText += chunk;
          try {
            // Try to parse the response as JSON
            const jsonStart = responseText.indexOf('{');
            const jsonEnd = responseText.lastIndexOf('}');
            
            if (jsonStart >= 0 && jsonEnd > jsonStart) {
              const jsonStr = responseText.substring(jsonStart, jsonEnd + 1);
              const data = JSON.parse(jsonStr);
              
              if (data.caption) {
                // Update the specific caption
                setCaptionSuggestions(prev => prev.map(c => 
                  c.id === captionId ? {
                    ...c,
                    text: data.caption,
                    isOverLimit: data.caption.length > characterLimit,
                    overrunAmount: data.caption.length > characterLimit ? data.caption.length - characterLimit : undefined
                  } : c
                ));
              }
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          console.error('Error refining caption:', error);
        }
      );
      
    } catch (err) {
      console.error('Error refining caption:', err);
    } finally {
      setRunningTool(null);
      setActiveFeedbackId(null);
      setFeedbackInput('');
    }
  };

  const handleInsertCaption = (caption: string) => {
    if (!activeTab) return;
    setCaption(activeTab, caption);
  };

  const handlePlatformAdjustment = (platform: string, content: string) => {
    setPlatformAdjustments(prev => 
      prev.map(adj => 
        adj.platform === platform 
          ? { ...adj, content } 
          : adj
      )
    );
  };
  
  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Caption Editor */}
      <div className="flex-1 space-y-6 caption-editor">
        {/* Platform Tabs */}
        <div className="border-b border-gray-200 dark:border-dark-700">
          <nav className="-mb-px flex space-x-8">
            {enabledPlatforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => setActiveTab(platform.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === platform.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {platform.name}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Caption Input */}
        {activeTab && (
          <div>
            <div className="flex justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Caption
              </label>
              <span className={`text-xs ${
                (captions[activeTab]?.length || 0) > (getPlatformConfig(activeTab)?.characterLimits.caption || 0)
                  ? 'text-red-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {captions[activeTab]?.length || 0}/{getPlatformConfig(activeTab)?.characterLimits.caption || 0}
              </span>
            </div>
            <textarea
              value={captions[activeTab] || ''}
              onChange={(e) => setCaption(activeTab, e.target.value)}
              rows={12}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white ${
                (captions[activeTab]?.length || 0) > (getPlatformConfig(activeTab)?.characterLimits.caption || 0)
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-dark-600'
              }`}
              placeholder={`Write your ${getPlatformConfig(activeTab)?.name} caption here...`}
            />
          </div>
        )}
        
        {/* Tool Results */}
        {toolResults.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              AI Tool Results
            </h3>
            <div className="space-y-2">
              <AnimatePresence>
                {toolResults.map((result) => (
                  <motion.div
                    key={result.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden"
                  >
                    <div 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 cursor-pointer"
                      onClick={() => toggleResultExpansion(result.id)}
                    >
                      <div className="flex items-center">
                        {result.toolId === 'hashtag-gen' && <Hash className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
                        {result.toolId === 'caption-ai' && <Pen className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
                        <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                          {result.title}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!result.expanded && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              applyResult(result);
                            }}
                            className="p-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                          >
                            Apply
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeResult(result.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {result.expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {result.expanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="p-3 border-t border-gray-200 dark:border-dark-700"
                        >
                          <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {result.content}
                          </pre>
                          
                          {/* Caption AI Results */}
                          {result.toolId === 'caption-ai' && captionSuggestions.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {captionSuggestions.map((suggestion) => (
                                <div 
                                  key={suggestion.id} 
                                  className="p-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <p className="text-gray-900 dark:text-white">{suggestion.text}</p>
                                    {suggestion.isOverLimit && (
                                      <span className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-full flex items-center">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        -{suggestion.overrunAmount} chars
                                      </span>
                                    )}
                                  </div>
                                  
                                  {activeFeedbackId === suggestion.id ? (
                                    <div className="mt-2 space-y-2">
                                      <textarea
                                        value={feedbackInput}
                                        onChange={(e) => setFeedbackInput(e.target.value)}
                                        placeholder="Tell us what to change..."
                                        className="w-full p-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg"
                                        rows={2}
                                      />
                                      <div className="flex justify-end space-x-2">
                                        <button
                                          onClick={() => setActiveFeedbackId(null)}
                                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleRefineCaptionFeedback(suggestion.id, feedbackInput)}
                                          disabled={!feedbackInput.trim() || runningTool !== null}
                                          className="px-2 py-1 text-xs bg-indigo-600 text-white rounded"
                                        >
                                          {runningTool ? 'Refining...' : 'Submit'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end space-x-2 mt-2">
                                      <button
                                        onClick={() => setActiveFeedbackId(suggestion.id)}
                                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                      >
                                        Refine
                                      </button>
                                      <button
                                        onClick={() => handleInsertCaption(suggestion.text)}
                                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded"
                                      >
                                        Insert
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => applyResult(result)}
                              className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                              Apply to Caption
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
      
      {/* AI Tools Drawer */}
      <div className={`ai-tools w-full md:w-80 flex-shrink-0 transition-all duration-300 ${
        drawerOpen ? 'opacity-100' : 'opacity-70 md:opacity-100'
      }`}>
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              AI Tools
            </h3>
            <button
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="md:hidden p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {drawerOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
          </div>
          
          <AnimatePresence>
            {drawerOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3"
              >
                {aiTools.map((tool) => (
                  <button
                    key={tool.id} 
                    onClick={() => tool.id === 'caption-ai' ? handleCaptionAI() : handleRunTool(tool.id)}
                    disabled={runningTool !== null}
                    className={`w-full flex items-start p-3 rounded-lg transition-colors ${
                      runningTool === tool.id 
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 cursor-wait'
                        : 'bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {runningTool === tool.id && (
                        <Loader2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 animate-spin" />
                      )}
                      {runningTool !== tool.id && tool.id === 'hashtag-gen' && (
                        <Hash className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      )}
                      {runningTool !== tool.id && tool.id === 'caption-ai' && (
                        <Pen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      )}
                    </div>
                    <div className="ml-3 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {tool.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {tool.description}
                      </p>
                    </div>
                  </button>
                ))}
                
                {/* Caption AI Settings (only shown when caption-ai is selected) */}
                {toolResults.some(result => result.toolId === 'caption-ai' && result.expanded) && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Number of Captions</h4>
                      <select
                        value={numCaptions}
                        onChange={(e) => setNumCaptions(parseInt(e.target.value))}
                        className="text-sm border border-gray-300 dark:border-dark-600 rounded-md p-1"
                      >
                        {[1, 2, 3, 4, 5].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 w-full">
        <button
          onClick={prevStep}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
        >
          <div className="flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </div>
        </button>
        <button
          onClick={nextStep}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Continue to Schedule
        </button>
      </div>
    </div>
  );
};

export default Step3CaptionAutomations;